/**
 * Converts a display name into a URL-safe slug segment.
 * Lowercases and strips every non-alphanumeric character (spaces, dots,
 * slashes, ampersands, etc.) so the result is always a single path segment.
 *
 * Used to derive partner profile route paths from partner names. Stripping all
 * punctuation (not just whitespace) keeps generation and matching in sync for
 * names like "U.S. Bank/Elavon" — a "/" would otherwise split the slug into
 * two path segments and break route matching.
 */
export const toSlug = (str: string): string => str.toLowerCase().replace(/[^a-z0-9]+/g, '');

/**
 * Extracts the internal path (pathname + search + hash) from a full URL.
 * Falls back to the original string if URL parsing fails.
 */
export const getInternalPath = (fullUrl: string): string => {
  try {
    const url = new URL(fullUrl);
    return url.pathname + url.search + url.hash;
  } catch {
    return fullUrl;
  }
};

/**
 * Validates whether a URL is safe to use as an iframe src.
 * Only allows http: and https: protocols.
 */
export const isSafeIframeUrl = (url: string | null | undefined): boolean => {
  if (!url) {
    return false;
  }

  const trimmed = url.trim();

  try {
    const parsed = new URL(trimmed, window.location.origin);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Normalizes a URL by ensuring it has a protocol (http:// or https://)
 * Only adds protocol to external URLs (e.g., www.example.com, example.com)
 * Internal/relative URLs (e.g., /content, ./page) are returned unchanged
 * @param url - The URL to normalize
 * @returns The normalized URL with protocol, or empty string if invalid
 */
export const normalizeExternalUrl = (url: string | undefined | null): string => {
  if (!url) return '';

  const trimmedUrl = url.trim();
  if (!trimmedUrl) return '';

  // If URL already has a protocol, return as-is
  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  // If URL is an internal/relative path, return as-is
  if (/^\.{0,2}\//.test(trimmedUrl)) {
    return trimmedUrl;
  }

  // If it looks like an external domain (contains a dot in the host part), prepend https://
  if (/^[^/]+\.[^/]+/.test(trimmedUrl)) {
    return `https://${trimmedUrl}`;
  }

  // For everything else (like 'dashboard', 'home'), treat as internal/relative
  return trimmedUrl;
};
